import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { 
  FolderOpen, 
  Plus, 
  Trash2, 
  Clock,
  FileBox
} from 'lucide-react';
import { projectsApi } from '../../lib/api';
import { useApp, UNITS } from '../../context/AppContext';
import { toast } from 'sonner';

const ProjectsModal = ({ open, onClose, mode = 'open' }) => {
  const { loadProject, currentProject, projectSettings, setProjectSettings, setActiveLeftTab } = useApp();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  // Default to current global unit (or meters) so users who already configured
  // it in Settings see their preferred unit pre-selected here.
  const [newProjectUnit, setNewProjectUnit] = useState(projectSettings?.unit || 'm');
  // Project type — drives which Library tab opens after creation
  const [newProjectType, setNewProjectType] = useState('projection');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (open) {
      loadProjects();
      setSearchQuery('');
      // Re-sync the unit picker with the current global setting whenever the
      // dialog reopens (in case it was changed in Settings since last open).
      setNewProjectUnit(projectSettings?.unit || 'm');
    }
  }, [open]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await projectsApi.getAll();
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      toast.error('Project name is required');
      return;
    }

    try {
      const response = await projectsApi.create({
        name: newProjectName.trim(),
        description: newProjectDesc.trim(),
        unit: newProjectUnit
      });
      loadProject(response.data);
      // Apply the chosen unit to the live editor immediately. Persisted via
      // autosave (project_settings.unit on the project doc).
      setProjectSettings(prev => ({ ...prev, unit: newProjectUnit, name: newProjectName.trim() }));
      // Auto-open the matching Library tab so the user can start placing
      // gear immediately without hunting for the right tab.
      if (setActiveLeftTab) {
        setActiveLeftTab(newProjectType === 'led' ? 'led' : 'projectors');
      }
      toast.success(`Project created · units: ${UNITS[newProjectUnit]?.label || newProjectUnit}`);
      onClose();
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const handleOpenProject = async (projectId) => {
    try {
      const response = await projectsApi.get(projectId);
      loadProject(response.data);
      toast.success('Project opened');
      onClose();
    } catch (error) {
      toast.error('Failed to open project');
    }
  };

  const handleDeleteProject = async (projectId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await projectsApi.delete(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      toast.success('Project deleted');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 max-w-[90vw] w-[1400px] max-h-[90vh]" data-testid="projects-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'new' || creating ? (
              <>
                <Plus className="w-5 h-5 text-fuchsia-400" />
                New Project
              </>
            ) : (
              <>
                <FolderOpen className="w-5 h-5 text-fuchsia-400" />
                Open Project
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {mode === 'new' || creating ? (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs text-zinc-400">Project Name</Label>
              <Input
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="My Projection Project"
                className="bg-zinc-800 border-zinc-700"
                autoFocus
                data-testid="new-project-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-zinc-400">Description (optional)</Label>
              <Input
                value={newProjectDesc}
                onChange={(e) => setNewProjectDesc(e.target.value)}
                placeholder="Project description..."
                className="bg-zinc-800 border-zinc-700"
                data-testid="new-project-desc-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-zinc-400">Project Type</Label>
              <div className="grid grid-cols-2 gap-2" data-testid="new-project-type-picker">
                <button
                  type="button"
                  onClick={() => setNewProjectType('projection')}
                  className={`px-3 py-2 rounded-md border text-xs transition-colors flex flex-col items-center justify-center ${
                    newProjectType === 'projection'
                      ? 'border-fuchsia-500 bg-fuchsia-500/15 text-white'
                      : 'border-zinc-700 bg-zinc-800/60 text-zinc-300 hover:border-zinc-500 hover:text-white'
                  }`}
                  data-testid="new-project-type-projection"
                >
                  <span className="font-semibold tracking-wider">Projection</span>
                  <span className="text-[10px] text-zinc-400 mt-0.5">Projector-based mapping</span>
                </button>
                <button
                  type="button"
                  onClick={() => setNewProjectType('led')}
                  className={`px-3 py-2 rounded-md border text-xs transition-colors flex flex-col items-center justify-center ${
                    newProjectType === 'led'
                      ? 'border-fuchsia-500 bg-fuchsia-500/15 text-white'
                      : 'border-zinc-700 bg-zinc-800/60 text-zinc-300 hover:border-zinc-500 hover:text-white'
                  }`}
                  data-testid="new-project-type-led"
                >
                  <span className="font-semibold tracking-wider">LED</span>
                  <span className="text-[10px] text-zinc-400 mt-0.5">LED wall builder</span>
                </button>
              </div>
              <p className="text-[10px] text-zinc-500">
                Opens the matching Library tab after creation.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-zinc-400">Measurement Units</Label>
              <div className="grid grid-cols-5 gap-2" data-testid="new-project-unit-picker">
                {Object.entries(UNITS).map(([key, cfg]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setNewProjectUnit(key)}
                    className={`px-2 py-2 rounded-md border text-xs transition-colors flex flex-col items-center justify-center ${
                      newProjectUnit === key
                        ? 'border-fuchsia-500 bg-fuchsia-500/15 text-white'
                        : 'border-zinc-700 bg-zinc-800/60 text-zinc-300 hover:border-zinc-500 hover:text-white'
                    }`}
                    data-testid={`new-project-unit-${key}`}
                  >
                    <span className="font-semibold uppercase tracking-wider">{key}</span>
                    <span className="text-[10px] text-zinc-400 mt-0.5">{cfg.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-zinc-500">
                You can change this anytime in Settings → World → Units.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="px-1 pb-2">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search projects..."
                className="bg-zinc-800 border-zinc-700 h-8 text-xs"
                autoFocus
                data-testid="project-search-input"
              />
            </div>
            <ScrollArea className="h-[65vh] pr-2">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-zinc-500">Loading projects...</div>
              </div>
            ) : projects.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <FileBox className="w-12 h-12 text-zinc-600 mb-4" />
                <div className="text-zinc-400 mb-2">No projects yet</div>
                <div className="text-xs text-zinc-500 mb-4">Create your first projection project</div>
                <Button
                  onClick={() => setCreating(true)}
                  className="bg-fuchsia-600 hover:bg-fuchsia-500"
                  data-testid="create-first-project-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Project
                </Button>
              </div>
            ) : (() => {
              const filtered = projects.filter(p => 
                p.name.toLowerCase().includes(searchQuery.toLowerCase())
              );
              return filtered.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-zinc-500 text-xs">
                  No projects match "{searchQuery}"
                </div>
              ) : (
              <div className="grid grid-cols-8 gap-2">
                {filtered.map(project => (
                  <div
                    key={project.id}
                    className={`aspect-square p-2 rounded-lg border cursor-pointer transition-all hover:scale-[1.03] flex flex-col justify-between ${
                      currentProject?.id === project.id
                        ? 'bg-fuchsia-500/15 border-fuchsia-500/50'
                        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-500'
                    }`}
                    onClick={() => handleOpenProject(project.id)}
                    data-testid={`project-item-${project.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[11px] leading-tight truncate">{project.name}</div>
                      <div className="text-[9px] text-zinc-500 mt-1 flex items-center gap-0.5">
                        <Clock className="w-2.5 h-2.5 flex-shrink-0" />
                        <span className="truncate">{formatDate(project.updated_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-end justify-between mt-1">
                      <div className="text-[9px] text-zinc-500 leading-tight">
                        <div>{project.scene_objects?.length || 0} obj</div>
                        <div>{project.projectors?.length || 0} proj</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 text-red-400/60 hover:text-red-300 hover:bg-red-500/10"
                        onClick={(e) => handleDeleteProject(project.id, e)}
                        data-testid={`delete-project-${project.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              );
            })()}
          </ScrollArea>
          </>
        )}

        <DialogFooter>
          {mode === 'new' || creating ? (
            <>
              <Button 
                variant="outline" 
                onClick={() => creating ? setCreating(false) : onClose()}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateProject}
                className="bg-fuchsia-600 hover:bg-fuchsia-500"
                data-testid="create-project-btn"
              >
                Create Project
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                onClick={onClose}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => setCreating(true)}
                className="bg-fuchsia-600 hover:bg-fuchsia-500"
                data-testid="new-project-modal-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectsModal;
